// AdminSchoolMessageDialog — admin sends a message to ONE school via either:
//   1. In-app inbox  → POST /api/inbox/admin/send  → the school sees a
//      celebratory "💌 ADMIN ସୂଚନା" pop-up next time they log in, then the
//      message lands in their /inbox tab.
//   2. WhatsApp Web  → wa.me/91<mobile>?text=... (only if school has a mobile).
//
// The dialog is opened from the per-row Action column of the Admin Schools
// list (see AdminDashboard.jsx). All inputs carry data-testid attributes.

import { useEffect, useState } from "react";
import axios from "axios";
import { toast } from "sonner";
import {
  Dialog, DialogContent, DialogDescription, DialogFooter, DialogHeader, DialogTitle,
} from "@/components/ui/dialog";
import { Button } from "@/components/ui/button";
import { Label } from "@/components/ui/label";
import { Input } from "@/components/ui/input";
import { MessageSquare, Phone, Send, X, Sparkles, Inbox } from "lucide-react";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

export default function AdminSchoolMessageDialog({ school, open, onClose }) {
  const [tab, setTab] = useState("inbox"); // "inbox" | "whatsapp"
  const [subject, setSubject] = useState("ADMIN ସୂଚନା · Update from Admin");
  const [body, setBody] = useState("");
  const [sending, setSending] = useState(false);

  useEffect(() => {
    if (!open) return;
    // Reset draft each time the dialog opens for a different school
    setSubject("ADMIN ସୂଚନା · Update from Admin");
    setBody("");
    setTab("inbox");
  }, [open, school?.id]);

  if (!school) return null;
  const hasMobile = /^\d{10}$/.test(school.mobile || "");

  const sendInbox = async () => {
    const trimmedBody = body.trim();
    if (!trimmedBody) { toast.error("Message body cannot be empty"); return; }
    if (!school.schoolId && !school.id) {
      toast.error("This account has no linked schoolId yet — cannot send to inbox.");
      return;
    }
    setSending(true);
    try {
      await axios.post(`${API}/inbox/admin/send`, {
        schoolId: school.schoolId || school.id,
        subject: subject.trim() || "Message from admin",
        body: trimmedBody,
      });
      toast.success("✅ Message sent — School will see a pop-up on next login.");
      onClose?.();
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Send failed");
    } finally {
      setSending(false);
    }
  };

  const sendWhatsApp = () => {
    if (!hasMobile) { toast.error("This school has no valid mobile on record"); return; }
    const text = encodeURIComponent(
      `${subject.trim() ? `*${subject.trim()}*\n\n` : ""}${body.trim()}\n\n— MDM Sahayak Admin Team`,
    );
    const url = `https://wa.me/91${school.mobile}?text=${text}`;
    window.open(url, "_blank", "noopener,noreferrer");
    toast.success("Opening WhatsApp Web…");
  };

  return (
    <Dialog open={open} onOpenChange={(o) => { if (!o) onClose?.(); }}>
      <DialogContent
        data-testid="admin-school-message-dialog"
        className="w-[calc(100vw-1.5rem)] max-w-lg overflow-hidden p-0"
      >
        <DialogHeader className="border-b border-border bg-gradient-to-r from-clay/15 via-white to-moss/15 px-5 py-3">
          <DialogTitle className="flex items-center gap-2 font-heading text-base font-bold text-ink-900">
            <MessageSquare className="h-4 w-4 text-clay" />
            Message · {school.schoolName}
          </DialogTitle>
          <DialogDescription className="text-xs">
            UDISE {school.udiseCode}{hasMobile ? ` · Mobile +91 ${school.mobile}` : " · No mobile on record"}
          </DialogDescription>
        </DialogHeader>

        <div className="space-y-3 px-5 py-4">
          {/* Channel tabs */}
          <div className="inline-flex w-full rounded-full border border-border bg-sand-50 p-1">
            <button
              type="button"
              onClick={() => setTab("inbox")}
              data-testid="msg-tab-inbox"
              className={`flex-1 rounded-full px-3 py-1.5 text-xs font-bold transition-colors ${tab === "inbox" ? "bg-clay text-white" : "text-ink-900"}`}
            >
              <Inbox className="mr-1 inline h-3.5 w-3.5" /> Inbox + Pop-up
            </button>
            <button
              type="button"
              onClick={() => setTab("whatsapp")}
              data-testid="msg-tab-whatsapp"
              disabled={!hasMobile}
              className={`flex-1 rounded-full px-3 py-1.5 text-xs font-bold transition-colors disabled:opacity-50 ${tab === "whatsapp" ? "bg-moss text-white" : "text-ink-900"}`}
            >
              <Phone className="mr-1 inline h-3.5 w-3.5" /> WhatsApp
            </button>
          </div>

          <div>
            <Label htmlFor="msg-subject" className="text-[11px] font-semibold uppercase tracking-[0.16em]">Subject {tab === "whatsapp" && <span className="text-ink-700 normal-case">(bold prefix)</span>}</Label>
            <Input id="msg-subject" value={subject} onChange={(e) => setSubject(e.target.value)} data-testid="msg-subject-input" />
          </div>
          <div>
            <Label htmlFor="msg-body" className="text-[11px] font-semibold uppercase tracking-[0.16em]">Message body</Label>
            <textarea
              id="msg-body"
              data-testid="msg-body-input"
              value={body}
              onChange={(e) => setBody(e.target.value)}
              placeholder={tab === "inbox"
                ? "ନମସ୍କାର ବିଦ୍ୟାଳୟ Team 🙏\n\nଆପଣଙ୍କ Setup ସମ୍ପୂର୍ଣ୍ଣ ହୋଇଯାଇଛି…"
                : "Hi! Quick update from the Admin team — please check your dashboard…"}
              rows={6}
              className="mt-1 w-full rounded-md border border-border bg-white px-3 py-2 text-sm outline-none focus:border-clay"
            />
            <p className="mt-1 text-[0.7rem] text-ink-700">
              {tab === "inbox"
                ? <><Sparkles className="mr-0.5 inline h-3 w-3" /> School will see a celebratory pop-up next login. Then the message stays in their Inbox.</>
                : <>Opens WhatsApp Web with the pre-filled message — review then send manually.</>}
            </p>
          </div>
        </div>

        <DialogFooter className="flex flex-col-reverse gap-2 border-t border-border bg-sand-50 px-5 py-3 sm:flex-row sm:items-center sm:justify-end">
          <Button variant="outline" onClick={onClose} data-testid="msg-cancel"><X className="mr-1 h-4 w-4" /> Cancel</Button>
          {tab === "inbox"
            ? <Button onClick={sendInbox} disabled={sending || !body.trim()} data-testid="msg-send-inbox" className="bg-clay text-white hover:bg-clay-dark"><Send className="mr-1 h-4 w-4" /> Send to Web Inbox</Button>
            : <Button onClick={sendWhatsApp} disabled={!body.trim() || !hasMobile} data-testid="msg-send-whatsapp" className="bg-moss text-white hover:bg-moss-dark"><Phone className="mr-1 h-4 w-4" /> Open WhatsApp</Button>}
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}
